import { HTTPClientInterface, HTTPRequest } from "@shared/core/HTTPClientInterface";
import { buildURL, SystemURL } from "./URLCatalog";

export interface CollaboratorCustomFieldListValue {
    id: string;
    label: string;
}

export interface CollaboratorCustomField {
    id: string;
    name: string;
    typeId: string;
    typeLabel: string;
    isRequired: boolean;
    isActive: boolean;
    values?: CollaboratorCustomFieldListValue[];
}

export interface CreateCollaboratorCustomFieldRequest {
    id: string;
    name: string;
    typeId: string;
    typeLabel: string;
    isRequired: boolean;
    isActive: boolean;
    values?: CollaboratorCustomFieldListValue[];
}

export interface UpdateCollaboratorCustomFieldRequest {
    name?: string;
    isRequired?: boolean;
    isActive?: boolean;
    values?: CollaboratorCustomFieldListValue[];
}

/** Endpoints of one kind of custom fields (collaborators, qontact points...). */
export interface CustomFieldsURLs {
    getFields: string;
    postField: string;
    patchField: string;
    deleteField: string;
}

/**
 * Custom field definitions CRUD, shared by every kind of custom fields.
 * The backend logic is the same for all of them (CustomFieldsController).
 */
export class CustomFieldsService {
    constructor(
        protected httpClient: HTTPClientInterface,
        private urls: CustomFieldsURLs,
    ) {}

    async getFields(orgID: string): Promise<CollaboratorCustomField[]> {
        const request = new HTTPRequest();
        request.url = buildURL(this.urls.getFields, { org_id: orgID });
        request.method = "GET";

        const response = await this.httpClient.execute(request);
        return response.result as CollaboratorCustomField[];
    }

    async createField(
        orgID: string,
        field: CreateCollaboratorCustomFieldRequest,
    ): Promise<CollaboratorCustomField> {
        const request = new HTTPRequest();
        request.url = buildURL(this.urls.postField, { org_id: orgID });
        request.method = "POST";
        request.body = field;

        const response = await this.httpClient.execute(request);
        return response.result as CollaboratorCustomField;
    }

    async updateField(
        orgID: string,
        fieldID: string,
        patch: UpdateCollaboratorCustomFieldRequest,
    ): Promise<CollaboratorCustomField> {
        const request = new HTTPRequest();
        request.url = buildURL(this.urls.patchField, {
            org_id: orgID,
            field_id: fieldID,
        });
        request.method = "PATCH";
        request.body = patch;

        const response = await this.httpClient.execute(request);
        return response.result as CollaboratorCustomField;
    }

    async deleteField(orgID: string, fieldID: string): Promise<void> {
        const request = new HTTPRequest();
        request.url = buildURL(this.urls.deleteField, {
            org_id: orgID,
            field_id: fieldID,
        });
        request.method = "DELETE";

        await this.httpClient.execute(request);
    }
}

export class CollaboratorCustomFieldsService extends CustomFieldsService {
    constructor(httpClient: HTTPClientInterface) {
        super(httpClient, {
            getFields: SystemURL.Users.getCustomFields,
            postField: SystemURL.Users.postCustomField,
            patchField: SystemURL.Users.patchCustomField,
            deleteField: SystemURL.Users.deleteCustomField,
        });
    }
}

export class QontactPointCustomFieldsService extends CustomFieldsService {
    constructor(httpClient: HTTPClientInterface) {
        super(httpClient, {
            getFields: SystemURL.QontactPoints.getCustomFields,
            postField: SystemURL.QontactPoints.postCustomField,
            patchField: SystemURL.QontactPoints.patchCustomField,
            deleteField: SystemURL.QontactPoints.deleteCustomField,
        });
    }

    /** Custom field values of one qontact point: { [fieldID]: value } */
    async getValues(orgID: string, qontactPointID: string): Promise<Record<string, string>> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.QontactPoints.getCustomFieldValues, {
            org_id: orgID,
            qp_id: qontactPointID,
        });
        request.method = "GET";

        const response = await this.httpClient.execute(request);
        return (response.result ?? {}) as Record<string, string>;
    }

    /** Sends ONLY the fields that changed; the backend merges them key by key. */
    async updateValues(
        orgID: string,
        qontactPointID: string,
        changedValues: Record<string, string>,
    ): Promise<Record<string, string>> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.QontactPoints.patchCustomFieldValues, {
            org_id: orgID,
            qp_id: qontactPointID,
        });
        request.method = "PATCH";
        request.body = changedValues;

        const response = await this.httpClient.execute(request);
        return (response.result ?? {}) as Record<string, string>;
    }
}
